import { AppContainer, ModalBox } from '@engyalo/delivery-ui-components'
import ReactGA from 'react-ga4'
import { Suspense, useEffect, useMemo } from 'react'
import { useLoadViews } from '../hooks/templates/useLoadViews'
import useTemplateComponentsState from '../hooks/templates/useTemplateComponentsState'
import { IModal, IModals } from '../interfaces'
import { RootState } from '../redux/store'
import { getCinnamonPropsContainer, useAppSelector, useModal } from '../hooks'
import EventEmitter from '../classes/EventEmiter'

interface IModalContainer {
  name: string
  modal: IModal
  eventEmitter: EventEmitter
  state: RootState
  modalStatus: IModals
}

const ModalContainer = ({
  name = '',
  modal,
  eventEmitter,
  state,
  modalStatus,
}: IModalContainer) => {
  const { closeModal, performModalActions } = useModal()
  const modals = useAppSelector((state) => state.defaultSlice.modals)
  const processedModal = useMemo(
    () => getCinnamonPropsContainer(modal, state, eventEmitter),
    [modal, state, eventEmitter],
  )
  const {
    uid: modalUid,
    header = { components: [] },
    body = { components: [] },
    footer = { components: [] },
    props = {},
  } = modal

  const { show } = modalStatus[name]
  const { props: headerProps, components: headerComponents } = header
  const { props: bodyProps, components: bodyComponents } = body
  const { props: footerProps, components: footerComponents } = footer
  useEffect(() => {
    if (show) {
      ReactGA.send({ hitType: 'pageview', page: `/${name}` })
      window.fbq('track', 'PageView', { page: `/${name}` })
    }
  }, [name, show])

  const {
    headerComponentsList,
    setHeaderComponentsList,
    bodyComponentsList,
    setBodyComponentsList,
    footerComponentsList,
    setFooterComponentsList,
  } = useTemplateComponentsState()

  useLoadViews({
    headerComponents,
    setHeaderComponentsList,
    bodyComponents,
    setBodyComponentsList,
    footerComponents,
    setFooterComponentsList,
    containerUid: modalUid,
  })

  const closeActions = () => {
    closeModal({ container: name, state })
    performModalActions({ container: name, modalStatus: !!modals?.[name].show })
  }

  return (
    <Suspense fallback={null}>
      <ModalBox
        id={modalUid}
        open={show}
        closeActions={closeActions}
        unmonuntOnClose
        styles={{
          zIndex: 1101,
        }}
        {...props}>
        <AppContainer
          modal
          header={{
            ...headerProps,
            id: `${modalUid}-header`,
            components: headerComponentsList.map(
              ({ uid, wrapper: Wrapper }) => (
                <Wrapper
                  parentUid={modalUid}
                  key={uid}
                  containerSection={processedModal.header}
                />
              ),
            ),
          }}
          body={{
            ...bodyProps,
            id: `${modalUid}-body`,
            components: bodyComponentsList.map(({ uid, wrapper: Wrapper }) => (
              <Wrapper
                parentUid={modalUid}
                key={uid}
                containerSection={processedModal.body}
              />
            )),
          }}
          footer={{
            ...footerProps,
            id: `${modalUid}-footer`,
            components: footerComponentsList.map(
              ({ uid, wrapper: Wrapper }) => (
                <Wrapper
                  parentUid={modalUid}
                  key={uid}
                  containerSection={processedModal.footer}
                />
              ),
            ),
          }}
        />
      </ModalBox>
    </Suspense>
  )
}

export default ModalContainer
